// Import centralized API configuration
import { API_URL } from '../config/api.config';

// API configuration - now centralized
const API_BASE_URL = API_URL;

// Default headers
const DEFAULT_HEADERS = {
  'Content-Type': 'application/json',
};

/**
 * Get auth token from localStorage
 * @returns {string|null} Auth token
 */
const getAuthToken = () => {
  const userData = localStorage.getItem('user');
  if (userData) {
    try {
      const parsed = JSON.parse(userData);
      return parsed.token;
    } catch (error) {
      console.error('Error parsing user data from localStorage:', error);
      return null;
    }
  }
  return null;
};

/**
 * Get headers with authentication
 * @returns {Object} Headers object
 */
const getAuthHeaders = () => {
  const token = getAuthToken();
  return {
    ...DEFAULT_HEADERS,
    ...(token && { Authorization: `Bearer ${token}` })
  };
};

/**
 * Handle API response
 * @param {Response} response - Fetch response
 * @returns {Promise<Object>} Parsed response data
 */
const handleResponse = async (response) => {
  const contentType = response.headers.get('content-type');
  let data;

  if (contentType && contentType.includes('application/json')) {
    data = await response.json();
  } else {
    data = { message: await response.text() };
  }

  if (!response.ok) {
    // Handle specific error cases
    if (response.status === 401) {
      // Token expired or invalid - clear local storage
      localStorage.removeItem('user');
      window.dispatchEvent(new Event('auth:logout'));
    }

    const error = new Error(data.message || `HTTP error! status: ${response.status}`);
    error.status = response.status;
    error.data = data;
    throw error;
  }

  return data;
};

/**
 * Make API request
 * @param {string} endpoint - API endpoint
 * @param {Object} options - Fetch options
 * @returns {Promise<Object>} API response
 */
const apiRequest = async (endpoint, options = {}) => {
  const url = `${API_BASE_URL}${endpoint}`;
  
  const config = {
    headers: getAuthHeaders(),
    ...options,
  };

  try {
    const response = await fetch(url, config);
    return await handleResponse(response);
  } catch (error) {
    console.error(`API request failed: ${config.method || 'GET'} ${url}`, error);
    throw error;
  }
};

// Authentication API calls
export const authAPI = {
  /**
   * Login user
   * @param {Object} credentials - Email and password
   * @returns {Promise<Object>} Login response
   */
  login: async (credentials) => {
    return apiRequest('/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
  },

  /**
   * Send passwordless login OTP by email or WhatsApp phone
   * @param {Object} data - Login method and identifier
   * @returns {Promise<Object>} OTP send response
   */
  sendLoginOTP: async (data) => {
    return apiRequest('/auth/send-login-otp', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * Verify passwordless login OTP
   * @param {Object} data - Login method, identifier, and OTP
   * @returns {Promise<Object>} Login response
   */
  verifyLoginOTP: async (data) => {
    return apiRequest('/auth/verify-login-otp', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * Get user profile
   * @returns {Promise<Object>} User profile
   */
  getProfile: async () => {
    return apiRequest('/auth/profile');
  },

  /**
   * Update user profile
   * @param {Object} profileData - Profile update data
   * @returns {Promise<Object>} Updated profile
   */
  updateProfile: async (profileData) => {
    return apiRequest('/auth/profile', {
      method: 'PUT',
      body: JSON.stringify(profileData),
    });
  },

  /**
   * Change password
   * @param {Object} passwordData - Current and new password
   * @returns {Promise<Object>} Password change response
   */
  changePassword: async (passwordData) => {
    return apiRequest('/auth/change-password', {
      method: 'PUT',
      body: JSON.stringify(passwordData),
    });
  },

  /**
   * Change temporary password (for first-time login of invited users)
   * @param {Object} passwordData - New password and confirmation
   * @returns {Promise<Object>} Password change response
   */
  changeTemporaryPassword: async (passwordData) => {
    return apiRequest('/auth/change-temporary-password', {
      method: 'PUT',
      body: JSON.stringify(passwordData),
    });
  },

  /**
   * Verify token
   * @returns {Promise<Object>} Token verification response
   */
  verifyToken: async () => {
    return apiRequest('/auth/verify');
  },

  /**
   * Send phone verification OTP (post-registration)
   * @param {Object} data - Phone number
   * @returns {Promise<Object>} OTP send response
   */
  sendPhoneVerificationOTP: async (data) => {
    return apiRequest('/auth/send-phone-verification-otp', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * Verify phone OTP (post-registration)
   * @param {Object} data - Phone number and OTP
   * @returns {Promise<Object>} Verification response
   */
  verifyPhoneOTP: async (data) => {
    return apiRequest('/auth/verify-phone-otp', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
};

// General API utility functions
export const api = {
  /**
   * GET request
   * @param {string} endpoint - API endpoint
   * @returns {Promise<Object>} API response
   */
  get: (endpoint) => apiRequest(endpoint),

  /**
   * POST request
   * @param {string} endpoint - API endpoint
   * @param {Object} data - Request data
   * @returns {Promise<Object>} API response
   */
  post: (endpoint, data) => apiRequest(endpoint, {
    method: 'POST',
    body: JSON.stringify(data),
  }),

  /**
   * PUT request
   * @param {string} endpoint - API endpoint
   * @param {Object} data - Request data
   * @returns {Promise<Object>} API response
   */
  put: (endpoint, data) => apiRequest(endpoint, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),

  /**
   * DELETE request
   * @param {string} endpoint - API endpoint
   * @returns {Promise<Object>} API response
   */
  delete: (endpoint) => apiRequest(endpoint, {
    method: 'DELETE',
  }),

  /**
   * Upload file
   * @param {string} endpoint - API endpoint
   * @param {FormData} formData - Form data with file
   * @param {string} method - HTTP method (default: 'POST')
   * @returns {Promise<Object>} API response
   */
  upload: (endpoint, formData, method = 'POST') => {
    const token = getAuthToken();
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    
    return fetch(`${API_BASE_URL}${endpoint}`, {
      method: method,
      headers,
      body: formData,
    }).then(handleResponse);
  },
};

// Error types for better error handling
export const API_ERRORS = {
  NETWORK_ERROR: 'NETWORK_ERROR',
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  AUTHENTICATION_ERROR: 'AUTHENTICATION_ERROR',
  AUTHORIZATION_ERROR: 'AUTHORIZATION_ERROR',
  NOT_FOUND: 'NOT_FOUND',
  SERVER_ERROR: 'SERVER_ERROR',
};

/**
 * Get error type from error object
 * @param {Error} error - Error object
 * @returns {string} Error type
 */
export const getErrorType = (error) => {
  if (!error.status) return API_ERRORS.NETWORK_ERROR;
  
  switch (error.status) {
    case 400:
      return API_ERRORS.VALIDATION_ERROR;
    case 401:
      return API_ERRORS.AUTHENTICATION_ERROR;
    case 403:
      return API_ERRORS.AUTHORIZATION_ERROR;
    case 404:
      return API_ERRORS.NOT_FOUND;
    case 409:
      return API_ERRORS.AUTHENTICATION_ERROR; // Conflict (e.g., user already exists)
    case 500:
    default:
      return API_ERRORS.SERVER_ERROR;
  }
};

export default { authAPI, api, API_ERRORS, getErrorType }; 